import React, { useEffect, useMemo, useState } from 'react'

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    LinearProgress,
    MenuItem,
    TextField,
    Tooltip,
    Typography,
} from '@material-ui/core'
import PlayArrowIcon from '@material-ui/icons/PlayArrow'
import ScheduleIcon from '@material-ui/icons/Schedule'
import { DateTime } from 'luxon'
import { toast } from 'react-toastify'

import { EzosLoader } from '../../../../../components/EzosLoader/EzosLoader'
import { ServiceSpecifyUnitsModal } from '../../../../../components/TemplateScheduleGantt/Components/DetailColumnServiceRow/ServiceSpecifyUnitsModal'
import { useTimezone } from '../../../../../contexts/timezone'
import { axiosInstance } from '../../../../../helpers'
import {
    AddTemplateServiceVendorParams,
    DeleteTemplateServiceParams,
    DeleteTemplateServiceVendorParams,
    TemplateWorkordersByPhaseEntry,
    UnitCoverageByPhaseEntry,
    UpdateEzScheduleParams,
    SetTemplateUnitsParams,
    UpdateTemplateServiceParams,
    UpdateTemplateServiceVendorParams,
} from '../../../../../hooks/useEzSchedule/types'
import {
    DoubleTransferChainResponse,
    ezScheduleDetails,
    AreaConfig,
    AreaStatusTag,
    Folder,
    ListVendor,
    ModelMap,
    TemplateService,
    TemplateWorkorder,
    UnitConfig,
    UnitCoverage,
} from '../../../../../models'
import { TurnReviewDialog } from '../components/TurnReviewDialog'
import { DoubleTransferChainModal } from '../../DoubleTransferChainModal'
import { useWalkScheduleStyles } from './styles'
import {
    clampedVendorUpdates,
    dateFieldErrors,
    enumerateDays,
    formatTimelineChip,
    workRangeMessage,
} from './timelineRules'
import { WalkScheduleBoard } from './WalkScheduleBoard'
import { WalkTimelinePicker } from './WalkTimelinePicker'
import { WalkUnitOrderDialog } from './WalkUnitOrderDialog'

interface Props {
    ezSchedule: ezScheduleDetails
    phaseId?: number
    inspectionId: number
    vendorList: ListVendor[]
    updateEzSchedule: (params: UpdateEzScheduleParams) => void
    updateEzScheduleAsync: (params: UpdateEzScheduleParams) => Promise<unknown>
    addTemplateServiceVendor: (params: AddTemplateServiceVendorParams) => void
    deleteTemplateService: (params: DeleteTemplateServiceParams) => void
    deleteTemplateServiceVendor: (
        params: DeleteTemplateServiceVendorParams,
    ) => void
    updateTemplateServiceVendor: (
        params: UpdateTemplateServiceVendorParams,
    ) => void
    updateTemplateService: (params: UpdateTemplateServiceParams) => void
    setTemplateUnits: (params: SetTemplateUnitsParams) => Promise<unknown>
    infrastructure: Folder
    unitConfigMap: ModelMap<UnitConfig>
    areaConfigMap: ModelMap<AreaConfig>
    areaStatusTagMap: ModelMap<AreaStatusTag>
    generateTemplateWorkorders: () => void
    generateTemplateWorkordersLoading: boolean
    templateWorkordersLoading: boolean
    unitCoverage?: UnitCoverage
    templateWorkordersByPhase: TemplateWorkordersByPhaseEntry[]
    unitCoverageByPhase: UnitCoverageByPhaseEntry[]
    generateWorkorders: (
        phaseIds: number[],
        deleteExistingWorkorders: boolean,
        creationSource: string,
    ) => Promise<{ status: number }>
    templateWorkorders: TemplateWorkorder[]
    reloadScheduleWorkorders: () => void | Promise<void>
    workordersLoading: boolean
    onCloseModal: () => void
    onAfterSchedule?: () => void
}

interface ScopeRow {
    template_service_id: number
    area_count: number
    unit_count: number
    unit_ids?: number[]
    estimated_cost?: number
}

const parseZoned = (value: string | null | undefined, zone: string) => {
    if (!value) return null
    const parsed = DateTime.fromISO(value).setZone(zone)
    return parsed.isValid ? parsed : null
}

const pickerDate = (date: DateTime | null) =>
    date?.setZone('local', { keepLocalTime: true }).toJSDate() ?? null

export const WalkScheduleStep = (props: Props) => {
    const {
        ezSchedule,
        phaseId,
        inspectionId,
        vendorList,
        updateEzSchedule,
        updateEzScheduleAsync,
        addTemplateServiceVendor,
        deleteTemplateService,
        deleteTemplateServiceVendor,
        updateTemplateServiceVendor,
        updateTemplateService,
        setTemplateUnits,
        infrastructure,
        unitConfigMap,
        areaConfigMap,
        areaStatusTagMap,
        generateTemplateWorkorders,
        generateTemplateWorkordersLoading,
        templateWorkordersLoading,
        unitCoverage,
        templateWorkordersByPhase,
        unitCoverageByPhase,
        generateWorkorders,
        templateWorkorders,
        reloadScheduleWorkorders,
        workordersLoading,
        onCloseModal,
        onAfterSchedule,
    } = props
    const classes = useWalkScheduleStyles()
    const { currentTimezone } = useTimezone()
    const [editing, setEditing] = useState(false)
    const [saving, setSaving] = useState(false)
    const [confirmEdit, setConfirmEdit] = useState(false)
    const [turnReviewOpen, setTurnReviewOpen] = useState(false)
    const [scheduleOpen, setScheduleOpen] = useState(false)
    const [scheduling, setScheduling] = useState(false)
    const [submitProgress, setSubmitProgress] = useState(0)
    const [waitingForWorkorders, setWaitingForWorkorders] = useState(false)
    const [schedulePhaseIds, setSchedulePhaseIds] = useState<number[]>([])
    const [deleteExistingWorkorders, setDeleteExistingWorkorders] = useState(
        !ezSchedule.use_multi_phase,
    )
    const [draftStart, setDraftStart] = useState<DateTime | null>(null)
    const [draftEnd, setDraftEnd] = useState<DateTime | null>(null)
    const [walkScope, setWalkScope] = useState<ScopeRow[]>([])
    const [dtChains, setDtChains] = useState<
        DoubleTransferChainResponse['chains']
    >([])
    const [dtReadyModalOpen, setDtReadyModalOpen] = useState(false)
    const [assignService, setAssignService] = useState<TemplateService | null>(
        null,
    )
    const [fitted, setFitted] = useState(false)
    const [timelineOverflows, setTimelineOverflows] = useState(false)
    const [orderOpen, setOrderOpen] = useState(false)
    const [ordering, setOrdering] = useState(false)

    const selectedPhase = useMemo(() => {
        if (phaseId === undefined) return ezSchedule.phases[0] ?? null
        return (
            ezSchedule.phases.find((phase) => phase.phase?.id === phaseId) ??
            null
        )
    }, [ezSchedule, phaseId])

    const services = selectedPhase?.template_services ?? []
    const workStart = parseZoned(
        phaseId === undefined
            ? ezSchedule.work_start_date
            : selectedPhase?.work_start_date,
        currentTimezone,
    )
    const workEnd = parseZoned(
        phaseId === undefined
            ? ezSchedule.work_end_date
            : selectedPhase?.work_end_date,
        currentTimezone,
    )
    const scheduleStart = parseZoned(ezSchedule.start_date, currentTimezone)
    const scheduleEnd = parseZoned(ezSchedule.end_date, currentTimezone)
    const transferStart = parseZoned(
        selectedPhase?.transfer_start_date,
        currentTimezone,
    )
    const transferEnd = parseZoned(
        selectedPhase?.transfer_end_date,
        currentTimezone,
    )
    const destReady = parseZoned(
        selectedPhase?.dest_unit_ready_date,
        currentTimezone,
    )
    const dtReady = parseZoned(
        selectedPhase?.dt_unit_ready_date,
        currentTimezone,
    )
    const hasTimeline = Boolean(workStart && workEnd)

    const scopeRefreshKey = services
        .map((service) => {
            const vendors = service.template_service_vendors
                .map((vendor) => vendor.id)
                .join('.')
            return `${service.id}:${service.budget}:${vendors}`
        })
        .join(';')

    useEffect(() => {
        let cancelled = false
        axiosInstance
            .get<ScopeRow[]>(`inspection/${inspectionId}/walk_scope/`)
            .then((response) => {
                if (!cancelled) setWalkScope(response.data ?? [])
            })
            .catch(() => {
                if (!cancelled) setWalkScope([])
            })
        return () => {
            cancelled = true
        }
    }, [inspectionId, scopeRefreshKey, generateTemplateWorkordersLoading])

    useEffect(() => {
        if (!ezSchedule.id) return
        let cancelled = false
        axiosInstance
            .get<DoubleTransferChainResponse>(
                `workorder/schedule/${ezSchedule.id}/double-transfer-chains/`,
            )
            .then((response) => {
                if (!cancelled) setDtChains(response.data?.chains ?? [])
            })
            .catch(() => {
                if (!cancelled) setDtChains([])
            })
        return () => {
            cancelled = true
        }
    }, [ezSchedule.id])

    const scopeByService = useMemo(
        () => new Map(walkScope.map((row) => [row.template_service_id, row])),
        [walkScope],
    )

    const includedUnitIds = useMemo(() => {
        const units =
            (phaseId === undefined
                ? ezSchedule.template_units
                : selectedPhase?.template_units) ?? []
        return new Set(
            units
                .filter((unit) => unit.is_included)
                .map((unit) => unit.unit.id),
        )
    }, [ezSchedule.template_units, phaseId, selectedPhase])

    const orderInfrastructure = useMemo(() => {
        const prune = (folder: Folder): Folder | null => {
            const units = (folder.units || []).filter((unit) =>
                includedUnitIds.has(unit.id),
            )
            const children = (folder.children || [])
                .map((child) => prune(child))
                .filter((child): child is Folder => child != null)
            if (units.length === 0 && children.length === 0) return null
            return { ...folder, units, children }
        }
        return prune(infrastructure)
    }, [infrastructure, includedUnitIds])

    const orderedUnits = useMemo(() => {
        const units =
            (phaseId === undefined
                ? ezSchedule.template_units
                : selectedPhase?.template_units) ?? []
        return units
            .filter((unit) => unit.is_included)
            .slice()
            .sort((left, right) => left.order - right.order)
            .map((unit) => ({
                unitId: unit.unit.id,
                name: unit.unit.name,
                folderName: unit.unit.folder?.name ?? '',
            }))
    }, [ezSchedule.template_units, phaseId, selectedPhase])

    const hasIncludedDt = dtChains.some((chain) => {
        const unitId = chain.dt.area?.unit_id
        return unitId != null && includedUnitIds.has(unitId)
    })

    const nonStandaloneCount = services.filter(
        (service) => !service.stand_alone,
    ).length
    const minDest =
        workStart && nonStandaloneCount > 0
            ? workStart.startOf('day').plus({ days: nonStandaloneCount })
            : null
    const minDt = (() => {
        const dest = destReady?.startOf('day') ?? minDest
        if (!dest || nonStandaloneCount < 1) return null
        return dest.plus({ days: nonStandaloneCount + 1 })
    })()

    const blockers = useMemo(() => {
        const messages: string[] = []
        if (!workStart) messages.push('Set a work start date.')
        if (!workEnd) messages.push('Set a work end date.')
        if (includedUnitIds.size === 0) {
            messages.push('Inspection units are still being applied.')
        }
        if (services.length === 0) messages.push('Add at least one service.')
        const missingVendors = services.filter(
            (service) => service.template_service_vendors.length === 0,
        )
        if (missingVendors.length > 0) {
            const names = missingVendors
                .slice(0, 3)
                .map((service) => service.service.name)
                .join(', ')
            const extra = missingVendors.length - 3
            messages.push(
                extra > 0
                    ? `Assign at least one vendor to every service (${names}, +${extra} more).`
                    : `Assign at least one vendor to every service (${names}).`,
            )
        }
        const fieldErrors = dateFieldErrors({
            workStart,
            workEnd,
            transferStart,
            transferEnd,
            destUnitReady: destReady,
            dtUnitReady: dtReady,
            bounds: { scheduleStart, scheduleEnd },
        })
        Object.entries(fieldErrors).forEach(([key, message]) => {
            if (message && key !== 'destUnitReady' && key !== 'dtUnitReady') {
                messages.push(message)
            }
        })
        const destTooEarly = Boolean(
            destReady &&
                minDest &&
                destReady.startOf('day') < minDest.startOf('day'),
        )
        const dtTooEarly = Boolean(
            destReady &&
                dtReady &&
                minDt &&
                dtReady.startOf('day') < minDt.startOf('day'),
        )
        if (
            hasIncludedDt &&
            (!destReady ||
                !dtReady ||
                destTooEarly ||
                dtTooEarly ||
                fieldErrors.destUnitReady ||
                fieldErrors.dtUnitReady)
        ) {
            messages.push(
                'You have double transfers in your schedule. You need to click the button to manage them.',
            )
        }
        return Array.from(new Set(messages))
    }, [
        workStart,
        workEnd,
        includedUnitIds,
        services,
        transferStart,
        transferEnd,
        destReady,
        dtReady,
        scheduleStart,
        scheduleEnd,
        minDest,
        minDt,
        hasIncludedDt,
    ])

    const days = workStart && workEnd ? enumerateDays(workStart, workEnd) : []
    const dayOffKeys = new Set(
        (selectedPhase?.day_off_list ?? [])
            .filter((dayOff) => dayOff.template_service_vendor == null)
            .map((dayOff) => {
                const parsed = parseZoned(dayOff.date, currentTimezone)
                return parsed ? parsed.toFormat('yyyy-LL-dd') : ''
            })
            .filter(Boolean),
    )

    const visibleWorkorders = useMemo(
        () =>
            selectedPhase?.template_is_valid === true ? templateWorkorders : [],
        [selectedPhase?.template_is_valid, templateWorkorders],
    )

    const workordersByVendorDate = useMemo(() => {
        const map = new Map<string, number>()
        visibleWorkorders.forEach((workorder) => {
            const date = parseZoned(workorder.start_date, currentTimezone)
            if (!date) return
            const key = `${workorder.template_service_vendor}-${date.toFormat(
                'yyyy-LL-dd',
            )}`
            map.set(key, (map.get(key) ?? 0) + 1)
        })
        return map
    }, [visibleWorkorders, currentTimezone])

    const averageByVendorId = useMemo(() => {
        const buckets = new Map<number, { areas: number; days: Set<string> }>()
        visibleWorkorders.forEach((workorder) => {
            const date = parseZoned(workorder.start_date, currentTimezone)
            if (!date) return
            const bucket = buckets.get(workorder.template_service_vendor) ?? {
                areas: 0,
                days: new Set<string>(),
            }
            bucket.areas += workorder.service_areas?.length ?? 0
            bucket.days.add(date.toFormat('yyyy-LL-dd'))
            buckets.set(workorder.template_service_vendor, bucket)
        })
        const averages: Record<number, number | null> = {}
        buckets.forEach((bucket, vendorId) => {
            averages[vendorId] =
                bucket.days.size > 0 ? bucket.areas / bucket.days.size : null
        })
        return averages
    }, [visibleWorkorders, currentTimezone])

    const assignInfrastructure = useMemo(() => {
        if (!assignService) return null
        const allowed = new Set(
            scopeByService.get(assignService.id)?.unit_ids ?? [],
        )
        const prune = (folder: Folder): Folder | null => {
            const units = (folder.units || []).filter(
                (unit) => allowed.has(unit.id) && includedUnitIds.has(unit.id),
            )
            const children = (folder.children || [])
                .map((child) => prune(child))
                .filter((child): child is Folder => child != null)
            if (units.length === 0 && children.length === 0) return null
            return { ...folder, units, children }
        }
        return prune(infrastructure)
    }, [assignService, infrastructure, scopeByService, includedUnitIds])

    const openEditor = () => {
        setDraftStart(workStart)
        setDraftEnd(workEnd)
        setEditing(true)
    }

    const requestEdit = () => {
        if (templateWorkorders.length > 0) {
            setConfirmEdit(true)
            return
        }
        openEditor()
    }

    const handleSaveTimeline = async () => {
        if (!draftStart || !draftEnd) return
        const start = draftStart.startOf('day')
        const end = draftEnd.endOf('day')
        setSaving(true)
        try {
            await updateEzScheduleAsync({
                work_start_date: start,
                work_end_date: end,
            })
            clampedVendorUpdates(
                services,
                start,
                end,
                currentTimezone,
            ).forEach((update) => updateTemplateServiceVendor(update))
            setEditing(false)
        } catch {
            toast.error('Could not save the project timeline.')
        } finally {
            setSaving(false)
        }
    }

    const templateIsValid = selectedPhase?.template_is_valid === true
    const canReview =
        templateIsValid &&
        !templateWorkordersLoading &&
        !generateTemplateWorkordersLoading &&
        templateWorkorders.length > 0
    const runDisabled =
        blockers.length > 0 ||
        generateTemplateWorkordersLoading ||
        saving ||
        (templateIsValid && templateWorkordersLoading)

    useEffect(() => {
        if (!scheduling) {
            if (!waitingForWorkorders) {
                setSubmitProgress(0)
            }
            return
        }

        const workorderCount = templateWorkorders.length
        const totalSeconds = Math.max(1, Math.ceil(workorderCount / 100))
        const totalMs = totalSeconds * 1000
        const startTime = Date.now()
        const intervalId = window.setInterval(() => {
            const elapsedMs = Date.now() - startTime
            const nextProgress = Math.min(
                100,
                Math.round((elapsedMs / totalMs) * 100),
            )
            setSubmitProgress(nextProgress)
        }, 200)

        return () => {
            window.clearInterval(intervalId)
        }
    }, [scheduling, waitingForWorkorders, templateWorkorders.length])

    useEffect(() => {
        if (!waitingForWorkorders || workordersLoading) {
            return
        }
        setWaitingForWorkorders(false)
        onAfterSchedule?.()
        onCloseModal()
        setScheduleOpen(false)
    }, [waitingForWorkorders, workordersLoading, onAfterSchedule, onCloseModal])

    const handleSchedule = async () => {
        const phaseIds =
            schedulePhaseIds.length > 0
                ? schedulePhaseIds
                : phaseId != null
                ? [phaseId]
                : []
        if (phaseIds.length === 0) return
        setScheduling(true)
        setSubmitProgress(0)
        try {
            const response = await generateWorkorders(
                phaseIds,
                deleteExistingWorkorders,
                'WALK_SCHEDULER_WEB',
            )
            if (response?.status === 201) {
                setSubmitProgress(100)
                setWaitingForWorkorders(true)
                await Promise.resolve(reloadScheduleWorkorders())
            }
        } catch {
            toast.error('Could not schedule work orders.')
        } finally {
            setScheduling(false)
        }
    }

    const scheduleBusy = scheduling || waitingForWorkorders

    const showPicker = !saving && (!hasTimeline || editing)
    const rangeError = workRangeMessage(
        draftStart ? draftStart.startOf('day') : null,
        draftEnd ? draftEnd.endOf('day') : null,
        { scheduleStart, scheduleEnd },
    )
    const boundsLabel =
        scheduleStart && scheduleEnd
            ? `Schedule dates ${scheduleStart.toFormat(
                  'LLL d',
              )} – ${scheduleEnd.toFormat('LLL d, yyyy')}`
            : null
    const scheduledCount = unitCoverage?.units_scheduled ?? 0
    const requiringCount = unitCoverage?.units_requiring_service
    const scheduledShort =
        typeof requiringCount === 'number' && scheduledCount < requiringCount

    const stats: [string, string, boolean][] = [
        [
            'Units in project',
            (unitCoverage?.units_in_project ?? '—').toLocaleString(),
            false,
        ],
        ['Units requiring', (requiringCount ?? '—').toLocaleString(), false],
        ['Units scheduled', scheduledCount.toLocaleString(), scheduledShort],
    ]

    const saveUnitOrder = async (unitIds: number[]) => {
        setOrdering(true)
        try {
            await setTemplateUnits({
                ordered_units: unitIds.map((unitId, index) => ({
                    unitId,
                    order: index,
                })),
            })
            setOrderOpen(false)
        } catch {
            toast.error('Could not save the unit order.')
        } finally {
            setOrdering(false)
        }
    }

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <div className={classes.leading}>
                    {hasTimeline && workStart && workEnd && (
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={requestEdit}
                            style={{ textTransform: 'none' }}
                        >
                            {formatTimelineChip(workStart, workEnd)}
                        </Button>
                    )}
                    {hasTimeline && !showPicker && timelineOverflows && (
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setFitted((current) => !current)}
                            style={{ textTransform: 'none' }}
                        >
                            {fitted ? 'Show Details' : 'Fit to Width'}
                        </Button>
                    )}
                    <div className={classes.stats}>
                        {stats.map(([label, value, warning]) => (
                            <div key={label} className={classes.stat}>
                                <span className={classes.statLabel}>
                                    {label}
                                </span>
                                <span
                                    className={classes.statValue}
                                    style={{
                                        color: warning ? '#d32f2f' : undefined,
                                    }}
                                >
                                    {value}
                                </span>
                            </div>
                        ))}
                        <Button
                            size="small"
                            variant="outlined"
                            disabled={orderedUnits.length === 0 || ordering}
                            onClick={() => setOrderOpen(true)}
                            style={{ textTransform: 'none' }}
                        >
                            Order Units
                        </Button>
                    </div>
                </div>
                <div className={classes.actions}>
                    {canReview ? (
                        <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={<ScheduleIcon />}
                            onClick={() => setTurnReviewOpen(true)}
                        >
                            Review & Schedule
                        </Button>
                    ) : (
                        <Tooltip
                            title={
                                blockers.length > 0 ? blockers.join(' ') : ''
                            }
                        >
                            <span>
                                <Button
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    disabled={runDisabled}
                                    startIcon={<PlayArrowIcon />}
                                    onClick={() => generateTemplateWorkorders()}
                                >
                                    {generateTemplateWorkordersLoading ||
                                    (templateIsValid &&
                                        templateWorkordersLoading)
                                        ? 'Running…'
                                        : 'Run EZ Scheduler'}
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                    {hasIncludedDt && (
                        <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            disabled={nonStandaloneCount < 1}
                            onClick={() => setDtReadyModalOpen(true)}
                            style={{ textTransform: 'none' }}
                        >
                            Manage double transfers
                        </Button>
                    )}
                </div>
            </div>
            {hasTimeline && !editing && blockers.length > 0 && (
                <div className={classes.banner}>{blockers[0]}</div>
            )}
            {saving ? (
                <div className={classes.body}>
                    <EzosLoader />
                </div>
            ) : showPicker ? (
                <WalkTimelinePicker
                    zone={currentTimezone}
                    start={draftStart}
                    end={draftEnd}
                    minDate={pickerDate(scheduleStart)}
                    maxDate={pickerDate(scheduleEnd)}
                    boundsLabel={boundsLabel}
                    error={rangeError}
                    saving={saving}
                    onChange={(start, end) => {
                        setDraftStart(start)
                        setDraftEnd(end)
                    }}
                    onSave={() => {
                        void handleSaveTimeline()
                    }}
                    onCancel={hasTimeline ? () => setEditing(false) : null}
                />
            ) : (
                workStart &&
                workEnd && (
                    <WalkScheduleBoard
                        zone={currentTimezone}
                        days={days}
                        services={services}
                        vendorList={vendorList}
                        scopeByService={scopeByService}
                        averageByVendorId={averageByVendorId}
                        workordersByVendorDate={workordersByVendorDate}
                        transferStart={transferStart}
                        transferEnd={transferEnd}
                        destReady={destReady}
                        dtReady={dtReady}
                        minDest={minDest}
                        minDt={minDt}
                        dayOffKeys={dayOffKeys}
                        updateEzSchedule={updateEzSchedule}
                        addTemplateServiceVendor={addTemplateServiceVendor}
                        deleteTemplateService={deleteTemplateService}
                        deleteTemplateServiceVendor={
                            deleteTemplateServiceVendor
                        }
                        updateTemplateServiceVendor={
                            updateTemplateServiceVendor
                        }
                        updateTemplateService={updateTemplateService}
                        onAssignUnits={setAssignService}
                        fitted={fitted}
                        onFittedChange={setFitted}
                        onOverflowChange={setTimelineOverflows}
                    />
                )
            )}
            {orderOpen && orderInfrastructure && (
                <WalkUnitOrderDialog
                    infrastructure={orderInfrastructure}
                    saving={ordering}
                    onClose={() => {
                        if (!ordering) setOrderOpen(false)
                    }}
                    onSave={(unitIds) => {
                        void saveUnitOrder(unitIds)
                    }}
                />
            )}
            <DoubleTransferChainModal
                open={dtReadyModalOpen}
                schedule={ezSchedule}
                onClose={() => setDtReadyModalOpen(false)}
                editReadyDates
                destUnitReadyDate={selectedPhase?.dest_unit_ready_date}
                dtUnitReadyDate={selectedPhase?.dt_unit_ready_date}
                minDestUnitReady={minDest}
                minDtUnitReady={minDt}
                maxReadyDate={workEnd}
                onUpdateReadyDates={updateEzSchedule}
            />
            {assignService && assignInfrastructure && (
                <ServiceSpecifyUnitsModal
                    open
                    onClose={() => setAssignService(null)}
                    infrastructure={assignInfrastructure}
                    templateService={assignService}
                    updateTemplateServiceVendor={updateTemplateServiceVendor}
                    unitConfigMap={unitConfigMap}
                    areaConfigMap={areaConfigMap}
                    areaStatusTagMap={areaStatusTagMap}
                />
            )}
            <Dialog
                open={confirmEdit}
                onClose={() => setConfirmEdit(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Change the project timeline?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Changing the timeline does not move work that was
                        already generated. Run EZ Scheduler again after you
                        save.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmEdit(false)}>
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={() => {
                            setConfirmEdit(false)
                            openEditor()
                        }}
                    >
                        Edit timeline
                    </Button>
                </DialogActions>
            </Dialog>
            <TurnReviewDialog
                dialogOpen={turnReviewOpen}
                handleDialogClose={() => setTurnReviewOpen(false)}
                templateWorkorders={templateWorkorders}
                unitCoverage={unitCoverage}
                selectedPhaseId={phaseId}
                schedulePhases={ezSchedule.schedule_phases ?? []}
                templateWorkordersByPhase={templateWorkordersByPhase}
                unitCoverageByPhase={unitCoverageByPhase}
                ezSchedule={ezSchedule}
                onSchedulePlannedWorkorders={(phaseIds) => {
                    setSchedulePhaseIds(phaseIds)
                    setDeleteExistingWorkorders(!ezSchedule.use_multi_phase)
                    setTurnReviewOpen(false)
                    setScheduleOpen(true)
                }}
            />
            <Dialog
                open={scheduleOpen}
                onClose={() => {
                    if (!scheduleBusy) setScheduleOpen(false)
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Schedule Workorders</DialogTitle>
                <DialogContent>
                    {scheduleBusy ? (
                        <>
                            <DialogContentText>
                                Scheduling workorders. This will take just a few
                                seconds.
                            </DialogContentText>
                            <LinearProgress
                                variant={
                                    waitingForWorkorders && !scheduling
                                        ? 'indeterminate'
                                        : 'determinate'
                                }
                                value={submitProgress}
                            />
                        </>
                    ) : (
                        <>
                            <DialogContentText>
                                Upon submission, the workorders will be
                                scheduled and when they finish, they will be on
                                your turn board.
                            </DialogContentText>
                            <DialogContentText>
                                You can choose to delete existing Assigned
                                workorders before scheduling. Only unlocked
                                workorders for services included in this run are
                                affected. Workorders in other statuses are not
                                impacted, no matter what you select.
                            </DialogContentText>
                            <TextField
                                select
                                fullWidth
                                margin="dense"
                                variant="outlined"
                                label="Delete existing assigned workorders?"
                                value={
                                    deleteExistingWorkorders
                                        ? 'delete_existing'
                                        : 'keep_existing'
                                }
                                onChange={(event) =>
                                    setDeleteExistingWorkorders(
                                        event.target.value ===
                                            'delete_existing',
                                    )
                                }
                            >
                                <MenuItem value="keep_existing">
                                    Do not delete any pre-existing assigned
                                    workorders
                                </MenuItem>
                                <MenuItem value="delete_existing">
                                    Delete pre-existing assigned workorders
                                </MenuItem>
                            </TextField>
                            <DialogContentText>
                                {deleteExistingWorkorders
                                    ? 'This only deletes unlocked Assigned workorders for services in this run.'
                                    : 'Existing assigned workorders stay. New work orders are added beside them.'}
                            </DialogContentText>
                        </>
                    )}
                </DialogContent>
                {!scheduleBusy && (
                    <DialogActions>
                        <Button
                            color="secondary"
                            variant="outlined"
                            onClick={() => setScheduleOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="primary"
                            variant="contained"
                            onClick={() => {
                                void handleSchedule()
                            }}
                        >
                            Submit
                        </Button>
                    </DialogActions>
                )}
            </Dialog>
        </div>
    )
}
